import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Part } from "../../models/Part";
import { deletePart, getPartById } from "../../repositories/PartsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	partIdForDelete: number | null;
};

export const DeletePart = ({ show, onHide, partIdForDelete }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [partForDelete, setPartForDelete] = useState<Part | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const classes = useStyles();

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);
		try {
			const res = await deletePart(partIdForDelete);

			if (res.ok) {
				setMessage("The part was deleted successfully");
				setMessageType("success");
				onHide(true);
				setPartForDelete(null);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const part = await getPartById(partIdForDelete);
				if (part.data?.data?.success) {
					setPartForDelete(part.data?.data?.result);
				} else {
					onHide(true);
					setMessage("Error, part not found. Try again");
					setOpenAlertMessage(true);
					setPartForDelete(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="deletePart"
					title={"Delete a part"}
					maxWidth="sm"
				>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "150px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="3rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Typography>
										You will not be able to recover deleted data
									</Typography>
								</Grid>
								<br />
								<Grid container spacing={1}>
									<Typography>
										Are you sure you want to delete{" "}
										<strong>{partForDelete?.partName}</strong>?
									</Typography>
								</Grid>
							</>
						)}
					</Modal.Content>
					{!isLoading && (
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setPartForDelete(null);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="secondary"
										onClick={handleDelete}
									>
										Delete
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					)}
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
